import { useState, useEffect } from 'react';
import { Menu, X, Bell } from 'lucide-react';
import { toolNavTarget } from './Dashboard';

const API = process.env.REACT_APP_API_URL || '';

// SPA navigation without a full reload: push the URL and fire a popstate so
// App.js's existing popstate handler swaps the page.
function spaNavigate(pageName, props = {}) {
  const path = pageName === 'dashboard' ? '/' : `/${pageName}`;
  window.history.pushState({ pageName, props }, '', path);
  window.dispatchEvent(new PopStateEvent('popstate', { state: { pageName, props } }));
}

// Standardized top bar for every tool page: a hamburger that opens the full
// tool-switcher drawer (left) and the OLOGY HQ logo (right). Self-contained —
// drop <TopNav /> in place of a tool's old back-button nav, no props needed.
// Tools with in-bar controls (e.g. a week switcher) can pass them as children,
// which render in the flexible middle between the hamburger and the logo.
export default function TopNav({ children }) {
  const [tools, setTools] = useState([]);
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [me, setMe] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/my-tools`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setTools(Array.isArray(d) ? d : []))
      .catch(() => {});
    fetch(`${API}/api/notifications?limit=1`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setUnread(d.unread || 0))
      .catch(() => {});
    fetch(`${API}/api/me`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setMe(d))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const go = (pageName, props) => { setOpen(false); spaNavigate(pageName, props); };

  const openTool = (t) => {
    const target = toolNavTarget(t);
    if (!target) return;
    if (target.url) { window.open(target.url, '_blank', 'noopener,noreferrer'); setOpen(false); }
    else go(target.page, { canUpload: t.has_upload_permission });
  };

  const signOut = async () => {
    setOpen(false);
    try { await fetch(`${API}/api/logout`, { method: 'POST', credentials: 'include' }); } catch {}
    window.location.href = '/';
  };

  const itemCls = 'w-full text-left px-5 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition';

  return (
    <>
      <nav className="bg-gray-900 px-4 sm:px-6 py-4 flex items-center gap-3 sticky top-0 z-30">
        <button onClick={() => setOpen(true)} aria-label="Open menu" className="text-gray-300 hover:text-white p-2 -ml-2 flex-shrink-0">
          <Menu size={24} />
        </button>
        <div className="flex-1 min-w-0">{children}</div>
        <button onClick={() => go('dashboard')} className="flex items-center gap-2 hover:opacity-80 transition flex-shrink-0">
          <span className="text-2xl font-bold" style={{ color: '#F05A28' }}>OLOGY</span>
          <span className="text-cream font-semibold text-xl">HQ</span>
        </button>
      </nav>

      {open && (
        <div className="fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} aria-hidden="true" />
          <aside className="relative w-72 max-w-[85vw] bg-gray-800 border-r border-gray-700 flex flex-col h-full shadow-2xl">
            <div className="px-5 py-4 border-b border-gray-700 flex items-center justify-between">
              <button onClick={() => go('dashboard')} className="flex items-center gap-2 hover:opacity-80 transition">
                <span className="text-xl font-bold" style={{ color: '#F05A28' }}>OLOGY</span>
                <span className="text-cream font-semibold text-lg">HQ</span>
              </button>
              <button onClick={() => setOpen(false)} aria-label="Close menu" className="text-gray-400 hover:text-white p-1 -mr-1">
                <X size={22} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-2">
              <button onClick={() => go('dashboard')} className={itemCls}>Dashboard</button>
              <button onClick={() => go('notifications')} className={`${itemCls} flex items-center gap-2`}>
                <Bell size={16} /> Notifications
                {unread > 0 && (
                  <span className="ml-auto text-[10px] font-semibold text-white rounded-full px-1.5 py-0.5 leading-none" style={{ backgroundColor: '#F05A28' }}>{unread}</span>
                )}
              </button>
              {tools.map((t) => (
                <button key={t.id} onClick={() => openTool(t)} className={itemCls}>{t.name}</button>
              ))}
            </div>

            <div className="px-5 py-4 border-t border-gray-700" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
              {me?.name && <div className="text-gray-400 text-xs mb-2 truncate">Welcome, {me.name}</div>}
              <div className="flex gap-4 items-center flex-wrap">
                <button onClick={() => go('notifications')} className="text-sm text-gray-400 hover:text-white transition flex items-center gap-1">
                  <Bell size={14} /> Notifications
                  {unread > 0 && (
                    <span className="ml-0.5 text-[10px] font-semibold text-white rounded-full px-1.5 py-0.5 leading-none" style={{ backgroundColor: '#F05A28' }}>{unread}</span>
                  )}
                </button>
                <button onClick={() => go('account')} className="text-sm text-gray-400 hover:text-white transition">My Account</button>
                <button onClick={signOut} className="text-sm text-gray-400 hover:text-white transition">Sign Out</button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
